import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { isPlacementDrag } from '../components/ProjectCanvas/utils/canvasPlacement'
import {
  pickNewTaskDropTarget,
  sameDropTarget,
  type NewTaskDropAccepts,
  type NewTaskDropSegment,
  type NewTaskDropTarget,
} from '../utils/newTaskDropTarget'
import {
  NEW_TASK_DRAG,
  ghostBirthAnimation,
  ghostBoxAt,
  ghostFallAnimation,
  landingAnimation,
  landingMakeRoomAnimation,
  type GhostAnimation,
} from '../utils/newTaskGhostMotion'
import { edgeScrollDelta } from '../utils/edgeScrollDelta'
import type { NewTaskDropState } from '../pages/ProjectPage/components/ProjectSegment/ProjectSegment.types'

/** Abstand der Eingabe im leeren Abschnitt zum Kopf (`ProjectSegment`, `firstTaskMargin`). */
const EMPTY_SEGMENT_OFFSET = 12

export interface UseNewTaskDragOptions {
  /** Sichtbarer Tab (TabStack-Falle): Nur dann wird gezogen, gemessen und auf Escape gehört. */
  isActive: boolean
  /** Der Scroller der Liste — er fährt am Rand mit. */
  scrollerRef: React.RefObject<HTMLElement | null>
  /** Der Rahmen um die Abschnitte (`[data-segment-idx]`). */
  listRef: React.RefObject<HTMLElement | null>
  /** Wo im Abschnitt eine neue Aufgabe landen darf, nach Index der gezeichneten Segmente. */
  accepts: (segmentIndex: number) => NewTaskDropAccepts
  /**
   * Loslassen über einem Ziel: anlegen (`useTaskCreation`, mit `landed`). `true`, wenn die Eingabe
   * aufgeht — sonst fällt die Fläche weg wie beim Abbrechen.
   */
  onDrop: (target: NewTaskDropTarget) => boolean
  /**
   * Was oben und unten im Scroller nicht zählt: Unten liegt die Pille über der Liste. Das Mitfahren
   * beginnt am Rand des Rests.
   */
  edgeInsets?: { top: number; bottom: number }
  disabled?: boolean
  /**
   * Was gezogen wird: eine neue Aufgabe (#301) oder ein neuer Abschnitt (#314). Der Abschnitt zielt am
   * zugeklappten Kopf auf „danach“ und trägt Kopfhöhe.
   */
  kind?: 'task' | 'section'
  /**
   * Nur Abschnitt: Der neue Kopf steht in der Liste. Bis dahin bleiben Lücke und Fläche nach dem Loslassen
   * stehen — der Kopf entsteht erst nach einem Schreibvorgang, nicht im selben Bild wie die Eingabe.
   */
  landed?: boolean
  /** Nur Abschnitt: der neue Kopf, der von der Fläche an seinen Platz gleitet. */
  landingSectionId?: string | null
}

/** So lange wartet ein abgelegter Abschnitt auf seinen Kopf, dann fällt die Fläche weg. */
const LANDING_TIMEOUT_MS = 1000

type Phase = 'idle' | 'dragging' | 'landing' | 'falling'

interface DragState {
  phase: Phase
  target: NewTaskDropTarget | null
  height: number
  tilt: number
}

const IDLE: DragState = { phase: 'idle', target: null, height: NEW_TASK_DRAG.rowHeight, tilt: 0 }

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

function play(el: HTMLElement | null, animation: GhostAnimation | null): Animation | null {
  if (!el || !animation || typeof el.animate !== 'function') return null
  return el.animate(animation.keyframes, animation.options)
}

/**
 * Die Liste, wie man sie SIEHT: je Abschnitt Kopf, Zeilen und offene Lücke — für
 * `pickNewTaskDropTarget`.
 */
function readGeometry(list: HTMLElement, accepts: (index: number) => NewTaskDropAccepts, target: NewTaskDropTarget | null, height: number) {
  const segments: NewTaskDropSegment[] = []
  let openGap: { target: NewTaskDropTarget; top: number; bottom: number } | null = null

  for (const segmentEl of list.querySelectorAll<HTMLElement>('[data-segment-idx]')) {
    const index = Number(segmentEl.dataset.segmentIdx)
    const header = segmentEl.querySelector<HTMLElement>(':scope > .section-header')
    const headerRect = header?.getBoundingClientRect()
    const collapsed = header?.classList.contains('section-header--collapsed') ?? false
    const mainList = collapsed ? null : segmentEl.querySelector<HTMLElement>('.task-list')
    const rows: NewTaskDropSegment['rows'][number][] = []
    let gapHost: Element | null = null
    for (const row of mainList?.querySelectorAll<HTMLElement>('.sortable-task-entry[data-task-id]') ?? []) {
      if (row.closest('.task-list') !== mainList) continue
      const rect = row.getBoundingClientRect()
      if (rect.height > 0) rows.push({ id: row.dataset.taskId!, top: rect.top, bottom: rect.bottom })
      // Die Lücke hinter einer Zeile ist ihr Geschwister in der Liste — auch wenn die Zeile gerade einläuft (Hülle).
      if (target?.kind === 'slot' && target.segmentIndex === index && target.afterTaskId === row.dataset.taskId) {
        let host: Element = row
        while (host.parentElement && host.parentElement !== mainList) host = host.parentElement
        gapHost = host.nextElementSibling
      }
    }
    const startsAt = headerRect ? headerRect.bottom + (segmentEl.dataset.sectionId ? EMPTY_SEGMENT_OFFSET : 0) : segmentEl.getBoundingClientRect().top
    segments.push({
      index,
      accepts: accepts(index),
      sectionId: segmentEl.dataset.sectionId ?? null,
      collapsedHeader: collapsed && headerRect ? { top: headerRect.top, bottom: headerRect.bottom } : null,
      rows,
      emptyAt: rows.length === 0 ? startsAt + height / 2 : null,
    })

    // „Danach“ (#314) öffnet die obere Lücke des zugeklappten Abschnitts, direkt unter seinem Kopf.
    if ((target?.kind === 'slot' || target?.kind === 'after') && target.segmentIndex === index) {
      const gap = target.kind === 'slot' && target.afterTaskId ? gapHost : segmentEl.querySelector(':scope > [data-new-task-gap]')
      if (gap?.hasAttribute('data-new-task-gap')) {
        const rect = gap.getBoundingClientRect()
        openGap = { target, top: rect.top, bottom: rect.bottom }
      }
    }
  }
  const bounds = list.getBoundingClientRect()
  return { segments, openGap, bounds: { left: bounds.left, right: bounds.right, top: bounds.top, bottom: bounds.bottom } }
}

/**
 * **„Neue Aufgabe“ aus der Listen-Werkzeug-Pille ziehen** (#301).
 *
 * Anfassen → ab 12px Bewegung ein Zug (`PLACEMENT_DRAG_THRESHOLD`, wie Notiz und Memo). Die Fläche
 * entsteht aus dem Button und hängt mittig am Zeiger; in der Liste öffnet sich die Lücke unter ihm,
 * am Rand fährt die Liste mit. Loslassen über einer Lücke legt dort an (`onDrop`) und lässt die
 * Eingabe von der Fläche an ihren Platz gleiten; Escape oder Loslassen daneben lässt die Fläche
 * wegfallen. Der Klick ohne Zug bleibt, wie er war.
 *
 * Eigener Zug statt dnd-kit: Die neue Aufgabe hat keinen Platz in dessen Listen. Window-Listener wie
 * `watchPlacementDrag` — der Button kann unter der Hand verschwinden.
 *
 * @example
 * const newTaskDrag = useNewTaskDrag({ isActive, scrollerRef: bodyMainRef, listRef: taskListRef, accepts, onDrop })
 * <ListToolbarDock onAddTaskPointerDown={newTaskDrag.onAddTaskPointerDown} addTaskDragging={newTaskDrag.dragging} … />
 * <TaskBoard newTaskDrop={newTaskDrag.drop} … />
 * {newTaskDrag.ghost && <NewTaskDragGhost {...newTaskDrag.ghost} />}
 */
export function useNewTaskDrag({
  isActive, scrollerRef, listRef, accepts, onDrop, edgeInsets = { top: 0, bottom: 0 }, disabled = false,
  kind = 'task', landed = false, landingSectionId = null,
}: UseNewTaskDragOptions) {
  const [state, setState] = useState<DragState>(IDLE)
  const stateRef = useRef(state)
  stateRef.current = state

  // Was die Listener brauchen, ohne bei jedem Rendern neu zu hängen.
  const latest = useRef({ accepts, onDrop, edgeInsets, isActive, kind, landingSectionId })
  latest.current = { accepts, onDrop, edgeInsets, isActive, kind, landingSectionId }

  const ghostRef = useRef<HTMLDivElement>(null)
  const pointerRef = useRef({ x: 0, y: 0 })
  const buttonRectRef = useRef<DOMRect | null>(null)
  const stopListeningRef = useRef<(() => void) | null>(null)
  const scrollFrameRef = useRef(0)
  /** Erst wenn der Zeiger einmal außerhalb der Ränder war, fährt die Liste mit — der Zug beginnt unten an der Pille. */
  const scrollArmedRef = useRef(false)
  const suppressClickRef = useRef(false)
  const landingFromRef = useRef<{ left: number; top: number; width: number; height: number; tilt: number } | null>(null)
  const [landingSeq, setLandingSeq] = useState(0)

  const ghostBox = useCallback(() => {
    const { height } = stateRef.current
    return ghostBoxAt(pointerRef.current, { width: NEW_TASK_DRAG.ghostWidth, height })
  }, [])

  const placeGhost = useCallback(() => {
    const ghost = ghostRef.current
    if (!ghost) return
    const box = ghostBox()
    ghost.style.left = `${box.left}px`
    ghost.style.top = `${box.top}px`
  }, [ghostBox])

  /** Neu zielen — State ändert sich nur, wenn das Ziel wechselt. */
  const retarget = useCallback(() => {
    const list = listRef.current
    const current = stateRef.current
    if (!list || current.phase !== 'dragging') return null
    const geometry = readGeometry(list, latest.current.accepts, current.target, current.height)
    const scrollerRect = scrollerRef.current?.getBoundingClientRect()
    const { top, bottom } = latest.current.edgeInsets
    const viewport = scrollerRect && scrollerRect.height > 0 ? { top: scrollerRect.top + top, bottom: scrollerRect.bottom - bottom } : undefined
    const next = pickNewTaskDropTarget({
      ...geometry, viewport, pointer: pointerRef.current, current: current.target, gapHeight: current.height,
      collapsedHeader: latest.current.kind === 'section' ? 'after' : 'end',
    })
    if (!sameDropTarget(next, current.target)) {
      stateRef.current = { ...current, target: next }
      setState(stateRef.current)
    }
    return next
  }, [listRef, scrollerRef])

  const stopScrolling = useCallback(() => {
    cancelAnimationFrame(scrollFrameRef.current)
    scrollFrameRef.current = 0
  }, [])

  /** Am Rand fährt die Liste mit, Bild für Bild, und nach jedem Schritt wird neu gezielt. */
  const edgeScroll = useCallback(() => {
    const scroller = scrollerRef.current
    if (!scroller || stateRef.current.phase !== 'dragging') return stopScrolling()
    const rect = scroller.getBoundingClientRect()
    if (rect.height === 0) return stopScrolling()
    const { top, bottom } = latest.current.edgeInsets
    const delta = edgeScrollDelta(pointerRef.current.y, { top: rect.top + top, bottom: rect.bottom - bottom })
    if (delta === 0) {
      scrollArmedRef.current = true
      return stopScrolling()
    }
    if (!scrollArmedRef.current) return stopScrolling()
    const before = scroller.scrollTop
    scroller.scrollTop = before + delta
    if (scroller.scrollTop === before) return stopScrolling()
    retarget()
    scrollFrameRef.current = requestAnimationFrame(edgeScroll)
  }, [scrollerRef, retarget, stopScrolling])

  const endSession = useCallback(() => {
    stopListeningRef.current?.()
    stopListeningRef.current = null
    stopScrolling()
    document.body.classList.remove('new-task-dragging')
  }, [stopScrolling])

  /**
   * Abbrechen. Mit Escape ist die Maus noch gedrückt: Ihr Loslassen über dem Button brächte noch den
   * Klick — der bleibt geschluckt, danach gilt jeder Klick wieder. Sonst (pointercancel, Tab gewechselt)
   * folgt kein Klick mehr, die Sperre fällt sofort (Diff-Audit 10.10.2026: der nächste Klick blieb stumm).
   */
  const cancel = useCallback((pointerStillDown: boolean) => {
    endSession()
    if (pointerStillDown) {
      const release = () => {
        window.removeEventListener('pointerup', release, true)
        window.removeEventListener('pointercancel', release, true)
        setTimeout(() => { suppressClickRef.current = false }, 0)
      }
      window.addEventListener('pointerup', release, true)
      window.addEventListener('pointercancel', release, true)
    } else {
      suppressClickRef.current = false
    }
    const current = stateRef.current
    if (current.phase !== 'dragging') return
    stateRef.current = { ...current, phase: 'falling', target: null }
    setState(stateRef.current)
  }, [endSession])

  const finish = useCallback(() => {
    endSession()
    const current = stateRef.current
    if (current.phase !== 'dragging') return
    const target = retarget() ?? null
    const box = ghostBox()
    // Die Lücken gehen mit dem Loslassen im selben Bild — an ihrer Stelle steht dann die Eingabe.
    const landed = target ? latest.current.onDrop(target) : false
    if (!landed) {
      stateRef.current = { ...stateRef.current, phase: 'falling', target: null }
      setState(stateRef.current)
      return
    }
    landingFromRef.current = { ...box, tilt: current.tilt }
    if (latest.current.kind === 'section') {
      // Der Kopf kommt erst nach dem Schreiben — bis dahin halten Lücke und Fläche den Platz (`landed`).
      stateRef.current = { ...stateRef.current, phase: 'landing', target }
      setState(stateRef.current)
      return
    }
    stateRef.current = IDLE
    setState(IDLE)
    setLandingSeq(seq => seq + 1)
  }, [endSession, retarget, ghostBox])

  const start = useCallback(() => {
    const tilt = NEW_TASK_DRAG.tilt * (Math.random() < 0.5 ? -1 : 1)
    const height = latest.current.kind === 'section' ? NEW_TASK_DRAG.sectionHeight : NEW_TASK_DRAG.rowHeight
    stateRef.current = { phase: 'dragging', target: null, height, tilt }
    setState(stateRef.current)
    scrollArmedRef.current = false
    // Ein Zug, der über dem Button endet, brächte sonst noch den Klick mit.
    suppressClickRef.current = true
    window.getSelection()?.removeAllRanges()
    document.body.classList.add('new-task-dragging')
  }, [])

  const onAddTaskPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0 || disabled || !latest.current.isActive || stateRef.current.phase !== 'idle') return
    const origin = { x: event.clientX, y: event.clientY }
    pointerRef.current = origin
    buttonRectRef.current = (event.currentTarget as HTMLElement).getBoundingClientRect()
    suppressClickRef.current = false
    stopListeningRef.current?.()

    const handleMove = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY }
      if (stateRef.current.phase === 'idle') {
        if (!isPlacementDrag(origin, pointerRef.current)) return
        start()
        return
      }
      placeGhost()
      retarget()
      if (!scrollFrameRef.current) edgeScroll()
    }
    const handleUp = (e: PointerEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY }
      if (stateRef.current.phase === 'dragging') {
        finish()
        // Der Klick folgt dem Loslassen in derselben Runde — danach gilt wieder jeder Klick.
        setTimeout(() => { suppressClickRef.current = false }, 0)
      } else {
        endSession()
      }
    }
    const handleCancel = () => {
      if (stateRef.current.phase === 'dragging') cancel(false)
      else endSession()
    }
    // Escape bricht ab — vor allen anderen (Capture), sonst höbe es im selben Zug die Auswahl auf.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || stateRef.current.phase !== 'dragging') return
      e.preventDefault()
      e.stopPropagation()
      cancel(true)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleCancel)
    window.addEventListener('keydown', handleKeyDown, true)
    stopListeningRef.current = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [disabled, start, placeGhost, retarget, edgeScroll, finish, cancel, endSession])

  /** Nach einem Zug kommt der Klick des Buttons noch an — einmal schlucken. */
  const consumeClick = useCallback(() => {
    if (!suppressClickRef.current) return false
    suppressClickRef.current = false
    return true
  }, [])

  // Die Fläche entsteht aus dem Button — gesetzt vor dem ersten Bild. Nur beim Beginn des Zugs, nicht
  // bei jedem Zielwechsel.
  const dragging = state.phase === 'dragging'
  useLayoutEffect(() => {
    if (!dragging) return
    placeGhost()
    const button = buttonRectRef.current
    if (!button) return
    play(ghostRef.current, ghostBirthAnimation(button, ghostBox(), stateRef.current.tilt, prefersReducedMotion()))
  }, [dragging, placeGhost, ghostBox])

  // Mit dem Mausrad gerollt: Unter dem stehenden Zeiger liegt eine andere Lücke.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!dragging || !scroller) return
    const handleScroll = () => { retarget() }
    scroller.addEventListener('scroll', handleScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', handleScroll)
  }, [dragging, scrollerRef, retarget])

  // Abgebrochen: Sie fällt weg, dann ist der Zug vorbei.
  useLayoutEffect(() => {
    if (state.phase !== 'falling') return
    const ghost = ghostRef.current
    ghost?.getAnimations?.().forEach(animation => animation.cancel())
    const fall = play(ghost, ghostFallAnimation(stateRef.current.tilt, prefersReducedMotion()))
    const done = () => {
      if (stateRef.current.phase !== 'falling') return
      stateRef.current = IDLE
      setState(IDLE)
    }
    if (!fall) { done(); return }
    fall.finished.then(done, done)
    return () => fall.cancel()
  }, [state.phase])

  /*
    Abschnitt abgelegt: Steht der neue Kopf in der Liste, ist der Zug im selben Bild vorbei — Lücke und Fläche
    gehen, der Kopf gleitet von der Fläche an seinen Platz (wie die Eingabe unten). Kommt er nicht, fällt die
    Fläche weg.
  */
  const landingDone = state.phase === 'landing' && landed
  useLayoutEffect(() => {
    if (!landingDone) return
    stateRef.current = IDLE
    setState(IDLE)
    setLandingSeq(seq => seq + 1)
  }, [landingDone])
  useEffect(() => {
    if (state.phase !== 'landing') return
    const timer = setTimeout(() => {
      if (stateRef.current.phase !== 'landing') return
      stateRef.current = { ...stateRef.current, phase: 'falling', target: null }
      setState(stateRef.current)
    }, LANDING_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [state.phase])

  /*
    Gelandet: Die Eingabe steht an ihrem Platz und gleitet von der Lage der Fläche dorthin. Sie steht
    höher da als die Lücke (ihr Rumpf wächst danach von selbst auf) — der Rest der Liste rückt im Takt
    des Aufklappens nach, statt zu springen.
  */
  useLayoutEffect(() => {
    const from = landingFromRef.current
    landingFromRef.current = null
    if (!from) return
    const sectionId = latest.current.landingSectionId
    const selector = kind === 'section'
      ? sectionId && `.section-header[data-section-id="${CSS.escape(sectionId)}"]`
      : '[data-new-task-landing]'
    const landing = selector ? listRef.current?.querySelector<HTMLElement>(selector) : null
    if (!landing) return
    const rect = landing.getBoundingClientRect()
    const reduced = prefersReducedMotion()
    play(landing, landingAnimation(from, { left: rect.left, top: rect.top, width: rect.width, height: rect.height }, from.tilt, reduced))
    play(landing, landingMakeRoomAnimation(from.height, rect.height, reduced))
  }, [landingSeq, listRef, kind])

  // Tab gewechselt oder ausgehängt mitten im Zug: abbrechen, nichts hängen lassen.
  useEffect(() => {
    if (!isActive && stateRef.current.phase === 'dragging') cancel(false)
  }, [isActive, cancel])
  useEffect(() => () => endSession(), [endSession])

  // Steht der neue Kopf schon da, sind Lücke und Fläche im selben Bild weg — nicht erst nach dem Effekt oben.
  const over = state.phase === 'idle' || landingDone
  const drop: NewTaskDropState | null = over ? null : { target: state.target, height: state.height }
  return {
    onAddTaskPointerDown,
    dragging,
    drop,
    ghost: over ? null : { ref: ghostRef, kind, width: NEW_TASK_DRAG.ghostWidth, height: state.height, tilt: state.tilt },
    consumeClick,
  }
}
